import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const Select = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  required = false,
  error,
  disabled = false,
  className = '',
  style = {},
  searchable = true,
  onSearch, // New prop for API search
  debounceMs = 300, // Debounce delay in milliseconds
  showClearButton = true, // New prop to control clear button visibility
  dropdownDirection = 'down', // New prop: 'up' or 'down'
  children,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const prevOptionsRef = useRef([]);

  // Debounced search effect
  const debouncedSearch = useCallback(
    (term) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        if (onSearch && term.trim()) {
          setIsLoading(true);
          try {
            const searchResults = await onSearch(term);
            setFilteredOptions(searchResults || []);
          } catch (error) {
            console.error('Search error:', error);
            setFilteredOptions([]);
          } finally {
            setIsLoading(false);
          }
        }
      }, debounceMs);
    },
    [onSearch, debounceMs]
  );

  // Filter options based on search term (for static options or when no onSearch provided)
  useEffect(() => {
    if (!onSearch) {
      if (children) {
        const childOptions = React.Children.toArray(children)
          .filter(child => child.type === 'option' && child.props.value !== '')
          .map(child => ({
            value: child.props.value,
            label: child.props.children
          }));
        
        if (searchTerm) {
          setFilteredOptions(
            childOptions.filter(opt =>
              opt.label.toString().toLowerCase().includes(searchTerm.toLowerCase())
            )
          );
        } else {
          setFilteredOptions(childOptions);
        }
      } else if (options.length > 0) {
        // Only update if options actually changed
        const optionsChanged = JSON.stringify(prevOptionsRef.current) !== JSON.stringify(options);
        if (optionsChanged) {
          prevOptionsRef.current = options;
          if (searchTerm) {
            setFilteredOptions(
              options.filter(opt =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
              )
            );
          } else {
            setFilteredOptions(options);
          }
        } else if (searchTerm) {
          // Just filter without causing re-render loop
          setFilteredOptions(
            options.filter(opt =>
              opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
          );
        }
      }
    }
  }, [searchTerm, children, onSearch, options]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleFocus = async () => {
    if (!disabled) {
      setIsOpen(true);
      setHighlightedIndex(-1);
      
      // Load initial data when dropdown is first opened (if using onSearch)
      if (onSearch && !children && !hasLoadedInitial) {
        setIsLoading(true);
        try {
          const initialResults = await onSearch('');
          // Merge with any existing options to ensure selected value is preserved
          const mergedOptions = [...options, ...initialResults];
          // Remove duplicates based on value
          const uniqueOptions = mergedOptions.filter((option, index, self) => 
            index === self.findIndex(o => o.value === option.value)
          );
          setFilteredOptions(uniqueOptions);
          setHasLoadedInitial(true);
        } catch (error) {
          console.error('Initial load error:', error);
          setFilteredOptions(options); // Fallback to provided options
          setHasLoadedInitial(true);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: '' } });
    setSearchTerm('');
    setHighlightedIndex(-1);
    if (onSearch) {
      // Reload initial options
      onSearch('').then(results => setFilteredOptions(results || []));
    }
  };

  const handleContainerClick = (e) => {
    // Prevent triggering when clicking on the clear button or dropdown options
    if (e.target.closest('button') || e.target.closest('.cursor-pointer') || isOpen) {
      return;
    }
    
    if (!disabled) {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      } else {
        handleFocus();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    
    const currentOptions = filteredOptions.length > 0 ? filteredOptions : 
                          options.length > 0 ? options : 
                          children ? React.Children.toArray(children)
                            .filter(child => child.type === 'option' && child.props.value !== '')
                            .map(child => ({ value: child.props.value, label: child.props.children })) : [];
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex(prev => 
            prev < currentOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(currentOptions.length - 1);
        } else {
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : currentOptions.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < currentOptions.length) {
          handleSelect(currentOptions[highlightedIndex].value);
        } else if (isOpen) {
          setIsOpen(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const getSelectedLabel = () => {
    if (!value) return placeholder;
    
    if (children) {
      const selectedChild = React.Children.toArray(children).find(
        child => child.props.value === value
      );
      return selectedChild ? selectedChild.props.children : placeholder;
    }
    
    // Check filteredOptions first (for dynamically loaded options)
    const filteredOption = filteredOptions.find(opt => opt.value === value);
    if (filteredOption) return filteredOption.label;
    
    // Fallback to static options
    const selectedOption = options.find(opt => opt.value === value);
    return selectedOption ? selectedOption.label : placeholder;
  };

  const getOptionClassName = (optionValue, index) => {
    let classes = 'px-3 py-2 cursor-pointer transition-colors text-sm ';
    
    if (value === optionValue) {
      classes += 'bg-primary-50 text-primary-700 font-medium';
    } else if (highlightedIndex === index) {
      classes += 'bg-blue-50 text-blue-700';
    } else {
      classes += 'hover:bg-gray-50 text-gray-900';
    }
    
    return classes;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef} style={style}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative" onClick={handleContainerClick}>
        <div className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
          <input
            type="text"
            className={`block w-full px-3 py-2 pr-16 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm ${
              error ? 'border-danger-500' : 'border-gray-300'
            }`}
            placeholder={placeholder}
            value={isOpen ? searchTerm : getSelectedLabel()}
            onChange={(e) => {
              const newSearchTerm = e.target.value;
              setSearchTerm(newSearchTerm);
              setHighlightedIndex(-1); // Reset highlight when typing
              if (searchable) {
                if (!isOpen) setIsOpen(true);
                if (onSearch) {
                  debouncedSearch(newSearchTerm);
                }
              }
            }}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            readOnly={!searchable}
            ref={searchInputRef}
            style={{
              color: !value && !isOpen ? '#9CA3AF' : 'inherit' // Light gray for placeholder text
            }}
          />
          {value && !disabled && showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg 
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform pointer-events-none ${
              dropdownDirection === 'up'
                ? (isOpen ? 'rotate-0' : 'rotate-180')
                : (isOpen ? 'rotate-180' : 'rotate-0')
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
        
        {isOpen && (
          <div className={`absolute z-[9999] w-full ${dropdownDirection === 'up' ? '-mt-1 bottom-full' : 'mt-1 top-full'} bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto`}>
            <div className="py-1">
              {isLoading ? (
                <div className="px-3 py-2 text-gray-500 text-center text-sm">
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Searching...
                  </div>
                </div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={getOptionClassName(option.value, index)}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </div>
                ))
              ) : options.length > 0 ? (
                options.map((option, index) => (
                  <div
                    key={option.value}
                    className={getOptionClassName(option.value, index)}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </div>
                ))
              ) : children ? (
                React.Children.map(children, (child, index) => (
                  child.props.value !== '' && (
                    <div
                      key={child.props.value}
                      className={getOptionClassName(child.props.value, index)}
                      onClick={() => handleSelect(child.props.value)}
                    >
                      {child.props.children}
                    </div>
                  )
                ))
              ) : (
                <div className="px-3 py-2 text-gray-500 text-center text-sm">
                  {searchTerm ? 'No results found' : 'No options available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <span className="text-danger-600 text-xs mt-1 block">{error}</span>}
    </div>
  );
};

export default Select;
